import { useState } from "react";

/**
 * Custom hook for managing toggle state with optional external control
 * @param {boolean} initialState - Initial toggle state (default: false)
 * @param {boolean} isControlled - Whether the toggle is externally controlled
 * @param {boolean} externalIsOpen - External open state (when controlled)
 * @param {function} externalOnToggle - External toggle handler (when controlled)
 * @returns {object} - Toggle state and handlers
 */
export function useToggle(
  initialState = false,
  isControlled = false,
  externalIsOpen,
  externalOnToggle,
) {
  const [internalIsOpen, setInternalIsOpen] = useState(initialState);

  // Use external control if provided, otherwise use internal state
  const isOpen = isControlled ? externalIsOpen : internalIsOpen;

  const toggle = isControlled
    ? externalOnToggle
    : () => setInternalIsOpen((prev) => !prev);

  const open = () => {
    if (isControlled) {
      // Can't directly open when externally controlled
      console.warn("Cannot directly open when toggle is externally controlled");
    } else {
      setInternalIsOpen(true);
    }
  };

  const close = () => {
    if (isControlled) {
      // Can't directly close when externally controlled
      console.warn(
        "Cannot directly close when toggle is externally controlled",
      );
    } else {
      setInternalIsOpen(false);
    }
  };

  return {
    isOpen,
    toggle,
    open,
    close,
  };
}

/**
 * Simplified hook for internal toggle management only
 * @param {boolean} initialState - Initial toggle state (default: false)
 */
export function useInternalToggle(initialState = false) {
  return useToggle(initialState, false);
}

/**
 * Hook for externally controlled toggle (just passes through the external controls)
 * @param {boolean} isOpen - External open state
 * @param {function} onToggle - External toggle handler
 */
export function useExternalToggle(isOpen, onToggle) {
  return useToggle(false, true, isOpen, onToggle);
}
